/**
 * <series-index-filter> — progressive enhancement over sections/series-index.liquid.
 *
 * The section renders the whole directory server-side: one native <details> per brand (closed, or open when
 * only one brand renders), every Series Page link inside, the total count line, the filter field (hidden) and
 * the no-match line (hidden). Without JS every brand still opens natively and every link is in the HTML.
 * This element adds:
 *   - the filter field (un-hidden here): debounced 120 ms; a series stays when every query word (lowercased,
 *     diacritics stripped, punctuation dropped) occurs in its brand + name; brands with no match hide,
 *     brands with a match open; the count reads "12 of 176 brake systems"; no match → the "Ask us" line
 *   - clearing the query restores the open/closed state the reader had before typing
 * Plural forms come from the section (data-count-{one,two,few,other} / data-count-filtered-*, rendered
 * server-side with {n} / {shown} slots); the form is picked with Intl.PluralRules on <html lang>.
 * Nothing is fetched or written via innerHTML; the element dispatches no events.
 */

/** @param {unknown} value */
const normalise = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

/** @param {string} value */
const words = (value) =>
  value
    .split(/\s+/)
    .map(normalise)
    .filter(Boolean);

class SeriesIndexFilter extends HTMLElement {
  /** @type {Array<{ group: HTMLDetailsElement, items: Array<{ item: HTMLElement, text: string }> }>} */
  #groups = [];
  /** @type {HTMLInputElement | null} */
  #input = null;
  /** @type {HTMLElement | null} */
  #count = null;
  /** @type {HTMLElement | null} */
  #none = null;
  /** @type {boolean[] | null} open state before the current query */
  #saved = null;
  /** @type {Intl.PluralRules | null} */
  #plural = null;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;
  #total = 0;
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;
    this.#input = this.querySelector('.series-index__filter');
    this.#count = this.querySelector('[data-count]');
    this.#none = this.querySelector('[data-none]');

    this.#groups = Array.from(this.querySelectorAll('details.series-index__brand')).map((group) => {
      const brand = group.querySelector('.series-index__brand-name')?.textContent ?? '';
      const items = Array.from(group.querySelectorAll('.series-index__list > li')).map((item) => ({
        item: /** @type {HTMLElement} */ (item),
        text: normalise(`${brand} ${item.textContent ?? ''}`),
      }));
      return { group: /** @type {HTMLDetailsElement} */ (group), items };
    });
    this.#total = this.#groups.reduce((sum, { items }) => sum + items.length, 0);

    try {
      this.#plural = new Intl.PluralRules(document.documentElement.lang || 'en');
    } catch {
      this.#plural = null;
    }

    const field = this.querySelector('[data-filter-field]');
    if (!this.#input || !field || this.#total === 0) return;
    field.removeAttribute('hidden');

    this.#input.addEventListener(
      'input',
      () => {
        clearTimeout(this.#timer);
        this.#timer = setTimeout(() => this.#apply(this.#input?.value ?? ''), 120);
      },
      { signal }
    );
    // the native clear (x) of type=search fires `search`, not always `input`
    this.#input.addEventListener('search', () => this.#apply(this.#input?.value ?? ''), { signal });
    // Enter never submits anything here
    this.#input.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Enter') event.preventDefault();
      },
      { signal }
    );

    if (this.#input.value) this.#apply(this.#input.value);
  }

  disconnectedCallback() {
    this.#controller.abort();
    clearTimeout(this.#timer);
  }

  /** @param {string} raw */
  #apply(raw) {
    const query = words(raw);

    if (query.length === 0) {
      for (const [index, { group, items }] of this.#groups.entries()) {
        group.hidden = false;
        for (const { item } of items) item.hidden = false;
        if (this.#saved) group.open = this.#saved[index] ?? group.open;
      }
      this.#saved = null;
      if (this.#none) this.#none.hidden = true;
      this.#writeCount(this.#total, null);
      return;
    }

    if (!this.#saved) this.#saved = this.#groups.map(({ group }) => group.open);

    let shown = 0;
    for (const { group, items } of this.#groups) {
      let matches = 0;
      for (const { item, text } of items) {
        const match = query.every((word) => text.includes(word));
        item.hidden = !match;
        if (match) matches += 1;
      }
      group.hidden = matches === 0;
      group.open = matches > 0;
      shown += matches;
    }

    if (this.#none) this.#none.hidden = shown > 0;
    this.#writeCount(this.#total, shown);
  }

  /**
   * @param {number} total
   * @param {number | null} shown null = no query
   */
  #writeCount(total, shown) {
    if (!this.#count) return;
    let form = 'other';
    try {
      form = this.#plural?.select(total) ?? (total === 1 ? 'one' : 'other');
    } catch {
      form = total === 1 ? 'one' : 'other';
    }
    const key = shown === null ? 'count' : 'countFiltered';
    const dataset = this.dataset;
    const formKey = `${key}${form.charAt(0).toUpperCase()}${form.slice(1)}`;
    const template = dataset[formKey] ?? dataset[`${key}Other`];
    if (!template) return;
    this.#count.textContent = template.replace('{n}', String(total)).replace('{shown}', String(shown ?? total));
  }
}

if (!customElements.get('series-index-filter')) {
  customElements.define('series-index-filter', SeriesIndexFilter);
}
